import { type ReactNode, useCallback, useEffect, useState } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Form } from '@opendoor/atoms/form';
import { Grid } from '@opendoor/atoms/grid';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import {
  ACCOUNT_FORM_SMS_CONSENT,
  normalizePhoneNumber,
  PHONE_FIELD_NAME,
  PhoneConsentFields,
  phoneNumberSchema,
  useSmsConsentState,
} from '@opendoor/auth-fe/forms';
import {
  IconCalendarClock,
  IconExclamationCircle,
  IconMail,
  IconPhone,
  type TablerIcon,
} from '@tabler/icons-react';
import { useForm } from '@tanstack/react-form';
import { z } from 'zod';

import { OpendoorLogoBadge } from '../landing-pages-v2/shared/OpendoorLogo';
import type { TourAvailabilityQuery } from './__generated__/tours.zeus';
import { AppHandoffPhone } from './AppHandoffPhone';
import { APP_DOWNLOAD_URL, AppStoreButton, buildConfirmationAttributedUrl } from './AppStoreButton';
import { TOUR_SHEET_COPY } from './tourBookingSheetCopy';

type AvailabilityDay = TourAvailabilityQuery['tours']['availability']['days'][number];
type AvailabilitySlot = AvailabilityDay['slots'][number];

export type ChosenSlot = {
  appointmentDate: string;
  appointmentTime: string;
  durationMinutes: number;
  /** Slot start rendered for the buyer, e.g. "10:00 AM - 10:30 AM". */
  label: string;
  /** Set for a slot restored from the login-return URL, before availability reconciliation. */
  fromReplay?: boolean;
};

export type TourHome = {
  street: string;
  /** City and state, e.g. "Litchfield Park, AZ". */
  locality: string | null;
  photoUrl: string | null;
};

// Availability DateTimes are UTC instants; the buyer and the booking input both
// need the listing's wall clock, which only Intl can produce from an instant.
const zonedParts = (
  dateTime: string,
  timezone: string | null,
): { date: string; hour: number; minute: string; second: string } => {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone: timezone ?? 'UTC',
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
      .formatToParts(new Date(dateTime))
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, part.value]),
  );
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    hour: Number(parts.hour),
    minute: parts.minute,
    second: parts.second,
  };
};

const formatTime = (dateTime: string, timezone: string | null): string => {
  const { hour, minute } = zonedParts(dateTime, timezone);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}:${minute} ${suffix}`;
};

const slotLabel = (start: string, end: string, timezone: string | null): string =>
  `${formatTime(start, timezone)} - ${formatTime(end, timezone)}`;

const timezoneAbbreviation = (timezone: string, at: Date): string | null => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    }).formatToParts(at);
    return parts.find((part) => part.type === 'timeZoneName')?.value ?? null;
  } catch {
    return null;
  }
};

const nextDate = (date: string): string => {
  const next = new Date(`${date}T12:00:00Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  return next.toISOString().slice(0, 10);
};

// "Today" and "Tomorrow" follow the listing's calendar, not the buyer's.
const dayTabLabel = (date: string, timezone: string | null): string => {
  const today = zonedParts(new Date().toISOString(), timezone).date;
  if (date === today) {
    return 'Today';
  }
  if (date === nextDate(today)) {
    return 'Tomorrow';
  }
  return new Date(`${date}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short' });
};

const selectionSummary = (slot: ChosenSlot): string => {
  const day = new Date(`${slot.appointmentDate}T12:00:00`).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  return `${day} at ${slot.label.split(' - ')[0]}`;
};

const SELF_TOUR_MINUTES = 30;

const StepHeading = ({
  title,
  body,
  className,
}: {
  title: string;
  body?: ReactNode;
  className?: string;
}) => (
  <Col className={cn('gap-3', className)}>
    <Text as="h2" className="text-subheader-lg text-od-content-primary md:pr-8 md:text-header-lg">
      {title}
    </Text>
    {body && (
      <Text as="p" className="text-pretty text-body-md text-od-content-secondary">
        {body}
      </Text>
    )}
  </Col>
);

const StatusBadge = ({ icon: Icon }: { icon: TablerIcon }) => (
  <Box className="size-16 shrink-0 items-center justify-center rounded-full bg-od-bg-tertiary">
    <Icon size={32} stroke={1.5} className="text-od-content-primary" aria-hidden />
  </Box>
);

const HomeCard = ({ home }: { home: TourHome }) => (
  <Row className="items-center gap-4 rounded-3xl bg-od-bg-secondary p-4">
    {home.photoUrl && (
      <img
        src={home.photoUrl}
        alt=""
        className="h-18 w-24 shrink-0 rounded-lg object-cover"
        loading="lazy"
      />
    )}
    <Col className="min-w-0 gap-0.5">
      <Text as="span" className="truncate text-label-md text-od-content-primary">
        {home.street}
      </Text>
      {home.locality && (
        <Text as="span" className="truncate text-body-sm text-od-content-secondary">
          {home.locality}
        </Text>
      )}
    </Col>
  </Row>
);

const DateTile = ({ date }: { date: string }) => {
  const day = new Date(`${date}T12:00:00`);
  return (
    <Col className="h-18 w-20 shrink-0 items-center justify-center gap-0.5 rounded-xl bg-od-bg-primary outline outline-od-border-primary">
      <Text as="span" className="text-label-sm text-od-content-secondary uppercase">
        {day.toLocaleDateString('en-US', { month: 'short' })}
      </Text>
      <Text
        as="span"
        className="text-subheader-xs leading-[110%] tracking-[-0.028em] text-od-content-primary"
      >
        {day.getDate()}
      </Text>
    </Col>
  );
};

/** The home and the chosen time in one card, as the sign-in step shows them. */
const TourCard = ({
  home,
  date,
  when,
  durationMinutes,
  zone,
}: {
  home?: TourHome | null;
  date: string;
  when: string;
  durationMinutes: number;
  zone: string | null;
}) => (
  <Col className="gap-3 rounded-xl bg-od-bg-secondary p-6">
    {home && (
      <>
        <Row className="items-center gap-4">
          {home.photoUrl && (
            <img
              src={home.photoUrl}
              alt=""
              className="h-15 w-20 shrink-0 rounded-lg object-cover"
              loading="lazy"
            />
          )}
          <Col className="min-w-0 gap-0.5">
            <Text as="span" className="truncate text-label-md text-od-content-primary">
              {home.street}
            </Text>
            {home.locality && (
              <Text as="span" className="truncate text-body-sm text-od-content-secondary">
                {home.locality}
              </Text>
            )}
          </Col>
        </Row>
        <Box className="h-px bg-od-border-secondary" />
      </>
    )}
    <Row className="items-center gap-4">
      <DateTile date={date} />
      <Col className="min-w-0 gap-0.5">
        <Text as="span" className="text-label-md text-od-content-primary">
          {when}
        </Text>
        <Text as="span" className="text-body-sm text-od-content-secondary">
          {TOUR_SHEET_COPY.slot.duration(durationMinutes)}
          {zone ? ` (${zone})` : ''}
        </Text>
      </Col>
    </Row>
  </Col>
);

export const toBookingInput = (slot: AvailabilitySlot, timezone: string | null): ChosenSlot => {
  // Rails joins these and parses in the listing timezone
  // (viewing_appointments_controller#scheduled_for), so the input must be the
  // listing-local wall clock, not the UTC instant the API returns.
  const start = zonedParts(slot.startTime, timezone);
  return {
    appointmentDate: start.date,
    appointmentTime: `T${String(start.hour).padStart(2, '0')}:${start.minute}:${start.second}`,
    durationMinutes: SELF_TOUR_MINUTES,
    label: slotLabel(slot.startTime, slot.endTime, timezone),
  };
};

// Mirrors the iOS TimeSlotGrid: three columns, grouped by time of day.
const timeOfDayGroup = (hour: number): string => {
  if (hour < 12) {
    return 'Morning';
  }
  if (hour < 17) {
    return 'Afternoon';
  }
  return 'Evening';
};

const slotGroups = (
  slots: AvailabilitySlot[],
  timezone: string | null,
): [string, AvailabilitySlot[]][] => {
  const groups: Record<string, AvailabilitySlot[]> = { Morning: [], Afternoon: [], Evening: [] };
  for (const slot of slots) {
    groups[timeOfDayGroup(zonedParts(slot.startTime, timezone).hour)].push(slot);
  }
  return Object.entries(groups).filter(([, slots]) => slots.length > 0);
};

export const SlotPickerStep = ({
  days,
  timezone,
  isLoading,
  onChoose,
  initialSlot,
}: {
  initialSlot?: ChosenSlot;
  days: AvailabilityDay[];
  timezone: string | null;
  isLoading: boolean;
  onChoose: (slot: ChosenSlot) => void;
}) => {
  const [selectedDate, setSelectedDate] = useState<string | null>(
    initialSlot?.appointmentDate ?? days[0]?.date ?? null,
  );
  const [selectedSlot, setSelectedSlot] = useState<ChosenSlot | null>(initialSlot ?? null);
  const selectedDay = days.find((day) => day.date === selectedDate) ?? days[0];

  const heading = (
    <StepHeading title={TOUR_SHEET_COPY.slot.heading} body={TOUR_SHEET_COPY.slot.subheading} />
  );

  if (isLoading) {
    return (
      <Col className="h-full gap-6 p-8 md:p-12">
        {heading}
        <Col className="flex-1 items-center justify-center">
          <Text as="span" className="text-body-md text-od-content-secondary">
            Loading tour times...
          </Text>
        </Col>
      </Col>
    );
  }

  const chipLabel = (slot: AvailabilitySlot): string => formatTime(slot.startTime, timezone);

  // Availability refetches under the open picker, so a stored choice is only
  // honored while its slot is still enabled in the latest data.
  const selectionEnabled =
    selectedSlot != null &&
    (selectedDay?.slots.some((slot) => {
      if (!slot.enabled) {
        return false;
      }
      const chosen = toBookingInput(slot, timezone);
      return (
        chosen.appointmentDate === selectedSlot.appointmentDate &&
        chosen.appointmentTime === selectedSlot.appointmentTime
      );
    }) ??
      false);

  const zoneAbbreviation = timezone
    ? timezoneAbbreviation(
        timezone,
        new Date(selectedDay?.slots[0]?.startTime ?? new Date().toISOString()),
      )
    : null;

  return (
    <Col className="min-h-0 flex-1">
      <Col
        role="region"
        aria-label="Tour dates and times"
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
      >
        <Col className="shrink-0 gap-6 px-8 pt-8 pb-6 md:px-12 md:pt-12">
          {heading}
          <Grid className="grid-cols-3 gap-2 md:grid-cols-5">
            {days.slice(0, 5).map((day) => {
              const isSelectedDay = day.date === selectedDay?.date;
              return (
                <Button
                  key={day.date}
                  variant={isSelectedDay ? 'secondaryInverse' : 'secondary'}
                  onClick={() => {
                    setSelectedDate(day.date);
                    setSelectedSlot(null);
                  }}
                  analyticsName="tours-day-chip"
                  aria-label={`Tour on ${day.date}`}
                  aria-pressed={isSelectedDay}
                  className="h-14 w-full flex-col gap-0.5 rounded-lg px-2"
                >
                  <Text as="span" className="text-label-md">
                    {dayTabLabel(day.date, timezone)}
                  </Text>
                  <Text
                    as="span"
                    className={cn(
                      'text-body-sm',
                      isSelectedDay
                        ? 'text-od-content-inverse-secondary'
                        : 'text-od-content-secondary',
                    )}
                  >
                    {new Date(`${day.date}T12:00:00`).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </Text>
                </Button>
              );
            })}
          </Grid>
        </Col>
        <Col className="shrink-0 gap-6 px-8 pb-6 md:px-12">
          {slotGroups(selectedDay?.slots ?? [], timezone).map(([group, slots]) => (
            <Col className="gap-3" key={group}>
              <Text as="h3" className="text-label-md text-od-content-primary">
                {group}
              </Text>
              <Grid className="grid-cols-3 gap-2 md:grid-cols-4 md:gap-y-3">
                {slots.map((slot) => {
                  const chosen = toBookingInput(slot, timezone);
                  const isSelected =
                    slot.enabled &&
                    selectedSlot?.appointmentDate === chosen.appointmentDate &&
                    selectedSlot?.appointmentTime === chosen.appointmentTime;
                  return (
                    <Button
                      key={slot.startTime}
                      variant={isSelected ? 'secondaryInverse' : 'secondary'}
                      onClick={() => setSelectedSlot(chosen)}
                      disabled={!slot.enabled}
                      aria-pressed={isSelected}
                      analyticsName="tours-slot-chip"
                      aria-label={
                        slot.enabled
                          ? chipLabel(slot)
                          : `${chipLabel(slot)} - ${TOUR_SHEET_COPY.slot.booked}`
                      }
                      className="h-11 w-full rounded-lg px-2 text-label-md disabled:not-aria-busy:bg-od-bg-inactive-secondary disabled:not-aria-busy:!text-od-content-inactive-primary"
                    >
                      {chipLabel(slot)}
                    </Button>
                  );
                })}
              </Grid>
            </Col>
          ))}
        </Col>
      </Col>
      <Col
        as="footer"
        className="relative shrink-0 border-t border-od-border-primary bg-od-bg-primary px-8 pt-4 pb-[34px] before:pointer-events-none before:absolute before:inset-x-0 before:-top-10 before:h-10 before:bg-linear-to-b before:from-white/0 before:to-white md:border-t-0 md:px-12 md:pt-0 md:pb-12 md:before:hidden"
      >
        <Row className="items-center justify-between gap-4 md:border-t md:border-od-border-secondary md:pt-6">
          <Col className="hidden min-w-0 gap-0.5 md:flex">
            <Text as="span" className="text-label-md text-od-content-primary">
              {selectionEnabled && selectedSlot
                ? selectionSummary(selectedSlot)
                : TOUR_SHEET_COPY.slot.noSelection}
            </Text>
            <Text as="span" className="text-body-sm text-od-content-secondary">
              {TOUR_SHEET_COPY.slot.duration(SELF_TOUR_MINUTES)}
              {zoneAbbreviation ? ` · Times in ${zoneAbbreviation}` : ''}
            </Text>
          </Col>
          <Button
            variant="primary"
            onClick={() => selectionEnabled && selectedSlot && onChoose(selectedSlot)}
            disabled={!selectionEnabled}
            aria-label="Book tour"
            analyticsName="tours-slot-review"
            className="w-full shrink-0 px-8 md:w-auto"
          >
            Book tour
          </Button>
        </Row>
      </Col>
    </Col>
  );
};

export const LoginStep = ({
  slot,
  home,
  timezone,
  onContinue,
}: {
  slot: ChosenSlot | null;
  home?: TourHome | null;
  timezone?: string | null;
  onContinue: (redirectTo: string) => void;
}) => {
  const handleClick = () => {
    const params = new URLSearchParams(window.location.search);
    if (slot) {
      // D7: the chosen slot rides through the login redirect and the sheet
      // replays it on return.
      params.set('tourDate', slot.appointmentDate);
      params.set('tourTime', slot.appointmentTime);
      params.set('tourDuration', String(slot.durationMinutes));
    }
    onContinue(
      `/login?redirectTo=${encodeURIComponent(
        `${window.location.pathname}${params.toString() ? `?${params}` : ''}`,
      )}`,
    );
  };
  return (
    <Col className="gap-6">
      <StepHeading title={TOUR_SHEET_COPY.login.heading} body={TOUR_SHEET_COPY.login.body} />
      {slot ? (
        <TourCard
          home={home}
          date={slot.appointmentDate}
          when={selectionSummary(slot)}
          durationMinutes={slot.durationMinutes}
          zone={
            timezone
              ? timezoneAbbreviation(timezone, new Date(`${slot.appointmentDate}T12:00:00Z`))
              : null
          }
        />
      ) : (
        home && <HomeCard home={home} />
      )}
      <Button
        variant="primary"
        onClick={handleClick}
        aria-label="login continue"
        analyticsName="tours-login-continue"
        className="w-full"
      >
        {TOUR_SHEET_COPY.login.continue}
      </Button>
    </Col>
  );
};

const withServerError =
  (message: string | undefined) =>
  <TMeta extends { errorMap: object }>(meta: TMeta): TMeta => ({
    ...meta,
    errorMap: { ...meta.errorMap, onServer: message },
  });

export const PhoneNumberStep = ({
  isSaving,
  saveFailed,
  onSave,
}: {
  isSaving: boolean;
  saveFailed: boolean;
  onSave: (phoneNumber: string, marketingOptIn: boolean, consentLanguage: string) => void;
}) => {
  const smsConsentState = useSmsConsentState(ACCOUNT_FORM_SMS_CONSENT);
  const form = useForm({
    defaultValues: { [PHONE_FIELD_NAME]: '' },
    validators: { onChange: z.object({ [PHONE_FIELD_NAME]: phoneNumberSchema }) },
    // A failed save renders as the field's own error; editing or resubmitting
    // clears it so a retry is never blocked by a stale server error.
    listeners: {
      onChange: ({ formApi }) => formApi.setFieldMeta(PHONE_FIELD_NAME, withServerError(undefined)),
    },
    onSubmit: async ({ value }) => {
      onSave(
        normalizePhoneNumber(value.phoneNumber),
        smsConsentState.optIn,
        smsConsentState.spec.version,
      );
    },
  });
  const setSaveError = useCallback(
    (message: string | undefined) => form.setFieldMeta(PHONE_FIELD_NAME, withServerError(message)),
    [form],
  );
  useEffect(
    () => setSaveError(saveFailed ? TOUR_SHEET_COPY.phone.saveFailed : undefined),
    [saveFailed, setSaveError],
  );
  return (
    <Col className="gap-6">
      <StepHeading
        title={TOUR_SHEET_COPY.phone.heading}
        body={TOUR_SHEET_COPY.phone.body}
        className="md:*:pr-8"
      />
      <Form.Root
        form={form}
        noValidate
        onSubmitCapture={() => setSaveError(undefined)}
        className="flex w-full flex-col gap-6"
      >
        <PhoneConsentFields
          consent={smsConsentState}
          analyticsPrefix="tours-phone"
          label={TOUR_SHEET_COPY.phone.label}
          inputSize="lg"
        />
        <Form.SubmitButton
          variant="primary"
          className="w-full"
          disabled={isSaving}
          analyticsName="tours-phone-save"
          aria-label="save phone number"
        >
          {TOUR_SHEET_COPY.phone.save}
        </Form.SubmitButton>
      </Form.Root>
    </Col>
  );
};

export const ConfirmationStep = ({
  addressToken,
}: {
  /** Listing address token for app-handoff badge attribution, when known. */
  addressToken?: string | null;
}) => {
  const appQrUrl = buildConfirmationAttributedUrl(
    APP_DOWNLOAD_URL,
    'confirmation-qr',
    addressToken,
  );
  return (
    <Col className="gap-6 md:gap-12 md:pt-10">
      <StepHeading
        className="md:px-12"
        title={TOUR_SHEET_COPY.confirmation.heading}
        body={
          <>
            <span className="md:hidden">{TOUR_SHEET_COPY.confirmation.bodyLead.mobile}</span>
            <span className="hidden md:inline">
              {TOUR_SHEET_COPY.confirmation.bodyLead.desktop}
            </span>
            {TOUR_SHEET_COPY.confirmation.body}
          </>
        }
      />
      <Box className="hidden md:flex md:flex-col">
        <AppHandoffPhone qrValue={appQrUrl} />
      </Box>
      <Box className="md:hidden">
        <AppStoreButton addressToken={addressToken} />
      </Box>
    </Col>
  );
};

const SupportRow = ({
  icon: Icon,
  href,
  analyticsName,
  children,
}: {
  icon: TablerIcon;
  href: string;
  analyticsName: string;
  children: string;
}) => (
  <Row className="items-center gap-2.5">
    <Icon size={16} className="shrink-0 text-od-content-secondary" aria-hidden />
    <Box
      href={href}
      aria-label={children}
      analyticsName={analyticsName}
      className="text-body-md leading-[140%] text-od-content-primary hover:underline"
    >
      {children}
    </Box>
  </Row>
);

/** The one error screen for any tour that can't be booked online. */
export const TourErrorStep = ({
  home,
  isRetrying,
  onRetry,
  retryAnalyticsName,
}: {
  home?: TourHome | null;
  isRetrying: boolean;
  onRetry: () => void;
  retryAnalyticsName: string;
}) => (
  <Col className="gap-6">
    <StatusBadge icon={IconExclamationCircle} />
    <StepHeading title={TOUR_SHEET_COPY.error.heading} body={TOUR_SHEET_COPY.error.body} />
    {home && (
      <Col className="md:hidden">
        <HomeCard home={home} />
      </Col>
    )}
    <Col className="gap-4 rounded-2xl bg-od-bg-secondary p-8 outline outline-od-border-primary">
      <Row className="items-start justify-between">
        <Text as="h3" className="text-label-md text-od-content-primary">
          {TOUR_SHEET_COPY.error.supportHeading}
        </Text>
        <Box className="text-od-content-accent-brand" aria-hidden>
          <OpendoorLogoBadge width="23px" />
        </Box>
      </Row>
      <Col className="gap-2.5">
        <Row className="items-center gap-2.5">
          <IconCalendarClock size={16} className="shrink-0 text-od-content-secondary" aria-hidden />
          <Text
            as="span"
            className="text-pretty text-body-md leading-[140%] text-od-content-primary"
          >
            {TOUR_SHEET_COPY.error.hours}
          </Text>
        </Row>
        <SupportRow
          icon={IconPhone}
          href={TOUR_SHEET_COPY.error.phoneHref}
          analyticsName="tours-error-call"
        >
          {TOUR_SHEET_COPY.error.phoneDisplay}
        </SupportRow>
        <SupportRow
          icon={IconMail}
          href={TOUR_SHEET_COPY.error.emailHref}
          analyticsName="tours-error-email"
        >
          {TOUR_SHEET_COPY.error.email}
        </SupportRow>
      </Col>
    </Col>
    <Button
      variant="primary"
      onClick={onRetry}
      disabled={isRetrying}
      analyticsName={retryAnalyticsName}
      aria-label={TOUR_SHEET_COPY.error.retry}
      className="w-full"
    >
      {TOUR_SHEET_COPY.error.retry}
    </Button>
  </Col>
);
